import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

const DALMA_VARIANT_TAG = /\[([a-z0-9_-]+)\]/i;

function normalizeDalmaVariantKey(value = '') {
  let normalized = String(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

  const numberedShade = normalized.match(/(?:^|\s)n(?:o|º|°)?\.?\s*0*(\d{1,3})(?=\s|$|[-—–])/i);
  if (numberedShade) return `n${numberedShade[1].padStart(2, '0')}`;

  const clipAliases = {
    'rosa claro': 'lightpink',
    'rosa intenso': 'darkpink',
  };
  if (clipAliases[normalized]) return clipAliases[normalized];

  normalized = normalized.split(/[—–]/)[0].trim();
  return normalized.replace(/[^a-z0-9]+/g, '');
}

function dalmaMediaTag(element) {
  const alt = element?.querySelector?.('img[alt]')?.getAttribute('alt') || '';
  const match = alt.match(DALMA_VARIANT_TAG);
  return match ? normalizeDalmaVariantKey(match[1]) : '';
}

function dalmaSelectedVariantKey(gallery) {
  const section = gallery.closest('.shopify-section') || document;
  const picker =
    section.querySelector('variant-picker[data-template-product-match="true"]') || section.querySelector('variant-picker');

  if (picker) {
    const checked = picker.querySelector('fieldset input:checked');
    if (checked instanceof HTMLInputElement && checked.value) return normalizeDalmaVariantKey(checked.value);

    const select = picker.querySelector('select');
    if (select instanceof HTMLSelectElement && select.value) return normalizeDalmaVariantKey(select.value);
  }

  return '';
}

function dalmaReindexActions(root, index) {
  const nodes = [root, ...root.querySelectorAll('*')];
  for (const node of nodes) {
    if (!(node instanceof Element)) continue;
    for (const attribute of ['on:click', 'on:pointerenter']) {
      const value = node.getAttribute(attribute);
      if (value && /\/\d+$/.test(value)) node.setAttribute(attribute, value.replace(/\/\d+$/, `/${index}`));
    }
  }
}

function dalmaStripTags(gallery) {
  for (const image of gallery.querySelectorAll('img[alt]')) {
    const alt = image.getAttribute('alt') || '';
    image.setAttribute('alt', alt.replace(/\s*\[[a-z0-9_-]+\]\s*/gi, ' ').replace(/\s{2,}/g, ' ').trim());
  }
}

function applyDalmaVariantMediaFilter(gallery) {
  const slideshow = gallery.querySelector('slideshow-component');
  const slides = Array.from(gallery.querySelectorAll('slideshow-slides > slideshow-slide'));
  if (!slides.length) return;

  let selectedKey = dalmaSelectedVariantKey(gallery);
  if (!selectedKey) selectedKey = dalmaMediaTag(slides[0]);
  if (!selectedKey) return;

  const keepIndices = [];
  slides.forEach((slide, index) => {
    if (dalmaMediaTag(slide) === selectedKey) keepIndices.push(index);
  });

  if (!keepIndices.length) return;

  const keepSet = new Set(keepIndices);

  slides.forEach((slide, index) => {
    if (!keepSet.has(index)) slide.remove();
  });

  for (const item of gallery.querySelectorAll('ul[data-testid="media-gallery-grid"] > li')) {
    if (dalmaMediaTag(item) !== selectedKey) item.remove();
  }

  for (const item of gallery.querySelectorAll('.dialog-thumbnails-list__thumbnail')) {
    if (dalmaMediaTag(item) !== selectedKey) item.remove();
  }

  for (const item of gallery.querySelectorAll('.dialog-zoomed-gallery > li')) {
    if (dalmaMediaTag(item) !== selectedKey) item.remove();
  }

  for (const controls of gallery.querySelectorAll('slideshow-controls[thumbnails]')) {
    const thumbnails = Array.from(controls.querySelectorAll('.slideshow-controls__thumbnail'));
    thumbnails.forEach((thumbnail) => {
      if (dalmaMediaTag(thumbnail) !== selectedKey) thumbnail.remove();
    });
  }

  for (const dotsList of gallery.querySelectorAll('.slideshow-controls__dots')) {
    const dots = Array.from(dotsList.children);
    dots.forEach((dot, index) => {
      if (!keepSet.has(index)) dot.remove();
    });
  }

  const remainingSlides = Array.from(gallery.querySelectorAll('slideshow-slides > slideshow-slide'));
  remainingSlides.forEach((slide, index) => {
    slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
    slide.style.setProperty('--slideshow-timeline', `--slide-${index}`);
    dalmaReindexActions(slide, index);
  });

  if (slideshow) {
    slideshow.setAttribute('initial-slide', '0');
    slideshow.style.setProperty(
      '--slideshow-timeline',
      remainingSlides.map((_, index) => `--slide-${index}`).join(',')
    );
  }

  for (const controls of gallery.querySelectorAll('slideshow-controls')) {
    const thumbnails = Array.from(controls.querySelectorAll('.slideshow-controls__thumbnail'));
    thumbnails.forEach((thumbnail, index) => {
      thumbnail.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
      dalmaReindexActions(thumbnail, index);
    });

    const dots = Array.from(controls.querySelectorAll('.slideshow-controls__dots button'));
    dots.forEach((dot, index) => {
      dot.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
      dot.style.setProperty('animation-timeline', `--slide-${index}`);
      dalmaReindexActions(dot, index);
    });

    const counter = controls.querySelector('.slideshow-controls__counter');
    if (counter) {
      counter.innerHTML = `<span ref="current">1</span><span class="slash">/</span>${remainingSlides.length}`;
    }

    if (remainingSlides.length <= 1) controls.hidden = true;
  }

  const gridItems = Array.from(gallery.querySelectorAll('ul[data-testid="media-gallery-grid"] > li'));
  gridItems.forEach((item, index) => dalmaReindexActions(item, index));

  const dialogThumbs = Array.from(gallery.querySelectorAll('.dialog-thumbnails-list__thumbnail'));
  dialogThumbs.forEach((item, index) => {
    item.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
    dalmaReindexActions(item, index);
  });

  const dialogMedia = Array.from(gallery.querySelectorAll('.dialog-zoomed-gallery > li'));
  dialogMedia.forEach((item, index) => dalmaReindexActions(item, index));

  if (remainingSlides.length <= 1) {
    for (const arrows of gallery.querySelectorAll('slideshow-arrows, .slideshow-controls__arrows')) arrows.hidden = true;
  }

  dalmaStripTags(gallery);
  gallery.dataset.dalmaVariantGroup = selectedKey;
}

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    applyDalmaVariantMediaFilter(this);
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
